import './ApiProvider.scss';

import AurigaLoader from '@components/Common/AurigaLoader';
import ApiContext from '@context/ApiContext';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useAuth } from '@utils/auth/AuthProvider';
import { ReactNode, useEffect, useState } from 'react';
import { ApiClients, ApiProviderProps } from 'types/ApiProvider.types';

import configureApiClients from './index';

const initialApiProviderProps: ApiProviderProps = {
  children: null,
};

/**
 * ApiProvider component
 *
 * This component serves as a context provider for all API-related functionality.
 * It initializes API clients and provides a consistent structure and shared state for API-related components.
 * @param {ReactNode} children - Child elements to be rendered within the provider
 * @returns {ReactNode} The APIProvider with rendered children
 * @example
 * <ApiProvider>
 *   <YourComponent />
 * </ApiProvider>
 */
const ApiProvider = ({ children = initialApiProviderProps.children }: ApiProviderProps): ReactNode => {
  const [apiClients, setApiClients] = useState<ApiClients | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<Error | null>(null);
  const { isAuthenticated, loading: authLoading, user: authUser } = useAuth();

  useEffect(() => {
    const initializeApis = async () => {
      if (!authLoading && isAuthenticated) {
        try {
          // Initialize API clients for use in Auriga
          const clients = configureApiClients(authUser?.attributes['custom:USERNAME'] || 'auriga-user');

          setApiClients(clients as ApiClients);
          setIsInitialized(true);
        } catch (error) {
          console.error('Failed to configure and initialize API clients: ', error);
          setInitError(error instanceof Error ? error : new Error('Unknown failure during API client initialization'));
        }
      } else if (!isAuthenticated) {
        // Reset API clients and initialization state if not authenticated
        setApiClients(null);
        setIsInitialized(false);
      }
    };

    initializeApis();
  }, [authLoading, authUser?.attributes, isAuthenticated]);

  if (initError) {
    return (
      <Box className='api-provider__error-container' data-testid='api-provider-error-container'>
        <Typography variant='h6' className='api-provider__error-text' gutterBottom>
          Error initializing API clients
        </Typography>
        <Typography className='api-provider__error-text'>{initError.message}</Typography>
      </Box>
    );
  }

  if (!isInitialized) {
    return (
      <AurigaLoader
        boxClassName='api-provider__container'
        textClassName='api-provider__loading-text'
        loaderText='Loading API clients...'
        data-test-id='api-provider-container'
      />
    );
  }

  return (
    <ApiContext value={{ apiClients, isInitialized, initError }}>
      <Box data-testid='api-provider'>{children}</Box>
    </ApiContext>
  );
};

export default ApiProvider;
